import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/crmApi";
import { PageHeader, Loader, Empty } from "@/crm/ui";
import { toast } from "sonner";
import { Mail, Plus, Pencil, Trash2, X } from "lucide-react";
import Modal from "@/crm/components/Modal";

const VARS_HINT = "Variables : {nom} {entreprise} {ville} {commercial} {contact} {email} {telephone}";

export default function EmailTemplates() {
  const [items, setItems] = useState(null);
  const [editing, setEditing] = useState(null); // null | {} | template

  const load = () => api.get("/email-templates").then(({ data }) => setItems(data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const del = async (id) => {
    try { await api.delete(`/email-templates/${id}`); toast.success("Template supprimé"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  if (!items) return <Loader />;

  return (
    <div>
      <PageHeader title="Templates d'emails" subtitle={VARS_HINT}
        actions={<button data-testid="new-template-btn" onClick={() => setEditing({})} className="flex items-center gap-2 px-3 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700"><Plus size={16} /> Nouveau template</button>} />

      {items.length === 0 ? <Empty label="Aucun template — créez des modèles réutilisables pour vos emails" icon={Mail} /> : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {items.map((t) => (
            <div key={t.id} className="bg-white rounded-xl border border-slate-200 p-5" data-testid={`template-${t.id}`}>
              <div className="flex items-start justify-between">
                <h3 className="font-heading font-semibold text-slate-800">{t.name}</h3>
                <div className="flex gap-1">
                  <button data-testid={`edit-template-${t.id}`} onClick={() => setEditing(t)} className="p-1.5 text-slate-400 hover:text-indigo-600"><Pencil size={15} /></button>
                  <button onClick={() => del(t.id)} className="p-1.5 text-slate-400 hover:text-rose-500"><Trash2 size={15} /></button>
                </div>
              </div>
              <div className="text-sm text-slate-600 mt-2 font-medium">{t.objet}</div>
              <p className="text-xs text-slate-400 mt-1 line-clamp-3 whitespace-pre-line">{t.corps}</p>
              <div className="text-[11px] text-slate-300 mt-3">{t.created_by_name} · {new Date(t.created_at).toLocaleDateString("fr-FR")}</div>
            </div>
          ))}
        </div>
      )}

      {editing !== null && <TemplateModal template={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />}
    </div>
  );
}

function TemplateModal({ template, onClose, onSaved }) {
  const isNew = !template.id;
  const [f, setF] = useState({ name: template.name || "", objet: template.objet || "", corps: template.corps || "" });

  const save = async () => {
    if (!f.name || !f.objet || !f.corps) return toast.error("Tous les champs sont requis");
    try {
      if (isNew) await api.post("/email-templates", f);
      else await api.put(`/email-templates/${template.id}`, f);
      toast.success(isNew ? "Template créé" : "Template mis à jour");
      onSaved();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  return (
    <Modal onClose={onClose} size="lg" testid="template-modal"
      label={isNew ? "Nouveau template" : "Modifier le template"}>
      <div className="flex items-center justify-between mb-4">
        <h3 className="font-heading font-bold text-lg">{isNew ? "Nouveau template" : "Modifier le template"}</h3>
        <button onClick={onClose}><X size={18} /></button>
      </div>
      <div className="space-y-3">
        <input data-testid="template-name" placeholder="Nom du template (ex: Relance prospect)" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
        <input data-testid="template-objet" placeholder="Objet de l'email" value={f.objet} onChange={(e) => setF({ ...f, objet: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
        <textarea data-testid="template-corps" placeholder="Corps de l'email…" rows={8} value={f.corps} onChange={(e) => setF({ ...f, corps: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-slate-300 text-sm" />
        <p className="text-xs text-slate-400">{VARS_HINT}</p>
      </div>
      <div className="flex justify-end gap-2 mt-4">
        <button onClick={onClose} className="px-4 py-2 rounded-lg border border-slate-300 text-sm">Annuler</button>
        <button data-testid="save-template" onClick={save} className="px-4 py-2 rounded-lg bg-indigo-600 text-white text-sm hover:bg-indigo-700">{isNew ? "Créer" : "Enregistrer"}</button>
      </div>
    </Modal>
  );
}
